import { useState } from 'react'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { LayoutDashboard, ChevronDown, ChevronUp } from 'lucide-react'
import { AlertBanner } from '../ui/AlertBanner'
import { KPICard } from '../ui/KPICard'
import { PageHeader } from '../ui/PageHeader'
import { YearSelector } from '../ui/YearSelector'
import { useExecutiveKPIs, useAvailableYears } from '../../hooks/useKPIs'
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext'
import { useBUContext } from '../../contexts/BUContext'
import { useThresholds, statusHighGood, statusLowGood } from '../../hooks/useThresholds'
import { RevenueChart, MonthlyRevenuePoint } from './RevenueChart'
import { NarrativeCard } from './NarrativeCard'
import { OrdersBreakdownCharts } from './OrdersBreakdownCharts'
import { BUBreakdownChart } from './BUBreakdownChart'
import { ShareModal } from './ShareModal'
import { formatKpiEur } from '../../lib/formatters'

function formatPct(value: number): string {
  return `${value.toFixed(1)}%`
}

function buildChartPlaceholder(): MonthlyRevenuePoint[] {
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
  const now = new Date()
  const currentMonth = now.getMonth()
  return months.slice(0, currentMonth + 1).map((mese) => ({ mese, corrente: 0 }))
}

function PanelHeader({
  label,
  children,
}: {
  label: string
  children?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
        <p className="section-label">{label}</p>
      </div>
      {children}
    </div>
  )
}

export function ExecutiveCockpit() {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number | 'cumulative'>(currentYear)
  const { data: availableYears = [currentYear] } = useAvailableYears()
  const { filters, apiDateFrom, apiDateTo } = useGlobalFilters()
  const { selectedBuId } = useBUContext()
  const { data: kpis, isLoading, error } = useExecutiveKPIs({
    year: selectedYear,
    apiDateFrom,
    apiDateTo,
    customer: filters.customer,
    service_line: filters.service_line,
    buId: selectedBuId,
  })
  const thr = useThresholds()
  const [showShare, setShowShare] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const revenueDelta =
    kpis && kpis.revenue_ytd_prev
      ? ((kpis.revenue_ytd - kpis.revenue_ytd_prev) / kpis.revenue_ytd_prev) * 100
      : undefined

  const dsoDelta =
    kpis && kpis.dso_days_prev
      ? ((kpis.dso_days - kpis.dso_days_prev) / kpis.dso_days_prev) * 100
      : undefined

  const MONTH_LABELS = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic']
  const visibleMonth = apiDateTo
    ? new Date(apiDateTo).getMonth() + 1
    : selectedYear === 'cumulative'
      ? 12
      : selectedYear < currentYear
        ? 12
        : new Date().getMonth() + 1

  const chartData: MonthlyRevenuePoint[] = kpis?.revenue_by_month?.length
    ? kpis.revenue_by_month
        .filter((p: { month: number }) => p.month <= visibleMonth)
        .map((p: { month: number; current: number; previous?: number | null }) => ({
          mese: MONTH_LABELS[p.month - 1],
          corrente: p.current,
          precedente: p.previous ?? undefined,
        }))
    : buildChartPlaceholder()

  const subtitle = kpis
    ? `Aggiornato al ${format(new Date(kpis.computed_at), "d MMM yyyy 'ore' HH:mm", { locale: it })}`
    : format(new Date(), "d MMM yyyy", { locale: it })

  return (
    <section aria-label="Executive Cockpit" className="space-y-6">
      {showShare && <ShareModal onClose={() => setShowShare(false)} />}
      <PageHeader
        title="Executive Cockpit"
        subtitle={subtitle}
        icon={LayoutDashboard}
        coveragePct={kpis?.coverage_score_pct}
        loading={isLoading}
        actions={
          <>
            <button
              onClick={() => setShowShare(true)}
              className="flex-shrink-0 rounded-lg border px-3 py-2 text-xs font-medium transition-colors hover:bg-white/5"
              style={{ borderColor: 'var(--km-border)', color: 'var(--km-text-secondary)' }}
            >
              Condividi
            </button>
            <YearSelector
              years={availableYears}
              value={selectedYear}
              onChange={setSelectedYear}
              loading={isLoading}
              disabled={!!(apiDateFrom && apiDateTo)}
            />
          </>
        }
      />

      {error && (
        <AlertBanner
          severity="critical"
          message="Impossibile caricare i KPI executive. Riprovare."
        />
      )}

      {/* KPI grid — 6 card */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <div className="stagger-1">
          <KPICard
            label="Revenue YTD"
            value={kpis ? formatKpiEur(kpis.revenue_ytd) : '—'}
            delta={revenueDelta !== undefined
              ? `${revenueDelta >= 0 ? '+' : ''}${revenueDelta.toFixed(1)}% vs anno prec.`
              : undefined}
            deltaPositive={revenueDelta !== undefined ? revenueDelta >= 0 : undefined}
            status={kpis
              ? statusHighGood(kpis.revenue_vs_budget_pct, thr.revenue_budget_positive, thr.revenue_budget_warning)
              : 'neutral'}
            loading={isLoading}
          />
        </div>

        <div className="stagger-2">
          <KPICard
            label="Revenue MTD"
            value={kpis ? formatKpiEur(kpis.revenue_mtd) : '—'}
            status={kpis
              ? statusHighGood(kpis.revenue_vs_budget_pct, thr.revenue_budget_positive, thr.revenue_budget_warning)
              : 'neutral'}
            loading={isLoading}
            subtext={kpis ? `${kpis.revenue_vs_budget_pct.toFixed(0)}% vs budget` : undefined}
          />
        </div>

        <div className="stagger-3">
          <KPICard
            label="Gross Margin %"
            value={kpis ? formatPct(kpis.gross_margin_pct) : '—'}
            status={kpis
              ? statusHighGood(kpis.gross_margin_pct, thr.margin_positive, thr.margin_warning)
              : 'neutral'}
            loading={isLoading}
          />
        </div>

        <div className="stagger-4">
          <KPICard
            label="DSO"
            value={kpis ? `${kpis.dso_days.toFixed(0)} gg` : '—'}
            delta={dsoDelta !== undefined
              ? `${dsoDelta >= 0 ? '+' : ''}${dsoDelta.toFixed(1)}% vs anno prec.`
              : undefined}
            deltaPositive={dsoDelta !== undefined ? dsoDelta <= 0 : undefined}
            status={kpis
              ? statusLowGood(kpis.dso_days, thr.dso_positive, thr.dso_warning)
              : 'neutral'}
            loading={isLoading}
            subtext="Days Sales Outstanding"
          />
        </div>

        <div className="stagger-5">
          <KPICard
            label="Crediti Scaduti"
            value={kpis ? formatKpiEur(kpis.overdue_amount) : '—'}
            status={kpis
              ? statusLowGood(kpis.overdue_pct, thr.overdue_positive, thr.overdue_warning)
              : 'neutral'}
            loading={isLoading}
            subtext={kpis ? `${formatPct(kpis.overdue_pct)} del totale` : undefined}
          />
        </div>

        <div className="stagger-6">
          <KPICard
            label="Backlog"
            value={kpis ? formatKpiEur(kpis.backlog_amount) : '—'}
            status="neutral"
            loading={isLoading}
            subtext={kpis ? `${kpis.active_projects} commesse attive` : undefined}
          />
        </div>
      </div>

      {/* Toggle KPI avanzati — SB.1: EBITDA stimato e cash runway */}
      <div>
        <button
          onClick={() => setShowAdvanced(v => !v)}
          className="flex items-center gap-2 text-xs font-medium transition-colors"
          style={{ color: 'var(--km-text-muted)' }}
          aria-expanded={showAdvanced}
          aria-controls="advanced-kpis"
        >
          {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          KPI avanzati
        </button>

        {showAdvanced && (
          <div id="advanced-kpis" className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div className="stagger-1">
              <KPICard
                label="EBITDA stimato"
                value={kpis ? formatKpiEur(kpis.ebitda ?? 0) : '—'}
                status={
                  kpis && kpis.ebitda !== undefined
                    ? statusHighGood(
                        kpis.ebitda > 0 ? 100 : kpis.ebitda === 0 ? 50 : 0,
                        80,
                        40,
                      )
                    : 'neutral'
                }
                loading={isLoading}
                subtext={
                  kpis
                    ? [
                        kpis.opex_ytd !== undefined ? `OPEX: ${formatKpiEur(kpis.opex_ytd)}` : null,
                        kpis.cost_entries_actual ? `Costi: ${formatKpiEur(kpis.cost_entries_actual)}` : null,
                      ].filter(Boolean).join(' · ') || undefined
                    : undefined
                }
              />
            </div>

            <div className="stagger-2">
              <KPICard
                label="Cash Runway"
                value={
                  kpis?.cash_not_configured
                    ? 'Non configurato'
                    : kpis?.cash_runway_months === null || kpis?.cash_runway_months === undefined
                    ? kpis ? '∞' : '—'
                    : `${kpis.cash_runway_months.toFixed(1)} mesi`
                }
                status={
                  kpis?.cash_not_configured || kpis?.cash_runway_months === null
                    ? 'neutral'
                    : kpis?.cash_runway_months !== undefined
                    ? statusLowGood(
                        kpis.cash_runway_months,
                        12,   // > 12 mesi = verde
                        3,    // < 3 mesi = rosso
                      )
                    : 'neutral'
                }
                loading={isLoading}
                subtext={
                  kpis?.burn_rate_monthly !== undefined
                    ? `Burn: ${formatKpiEur(kpis.burn_rate_monthly)}/mese`
                    : undefined
                }
              />
            </div>

            {/* SB.4 — NRR */}
            <div className="stagger-3">
              <KPICard
                label="NRR"
                value={
                  kpis?.nrr_pct === null || kpis?.nrr_pct === undefined
                    ? kpis ? 'N/D' : '—'
                    : `${kpis.nrr_pct.toFixed(1)}%`
                }
                status={
                  kpis?.nrr_pct !== null && kpis?.nrr_pct !== undefined
                    ? statusHighGood(kpis.nrr_pct, 100, 80)
                    : 'neutral'
                }
                loading={isLoading}
                subtext="Net Revenue Retention"
              />
            </div>

            {/* SB.4 — Churn rate */}
            <div className="stagger-4">
              <KPICard
                label="Churn rate"
                value={
                  kpis?.churn_insufficient_data
                    ? 'Dati insuff.'
                    : kpis?.churn_rate_pct === null || kpis?.churn_rate_pct === undefined
                    ? kpis ? 'N/D' : '—'
                    : `${kpis.churn_rate_pct.toFixed(1)}%`
                }
                status={
                  !kpis?.churn_insufficient_data && kpis?.churn_rate_pct !== null && kpis?.churn_rate_pct !== undefined
                    ? statusLowGood(kpis.churn_rate_pct, 5, 15)
                    : 'neutral'
                }
                loading={isLoading}
                subtext={
                  kpis?.ltv !== null && kpis?.ltv !== undefined
                    ? `LTV: ${formatKpiEur(kpis.ltv)}`
                    : 'Clienti ricorrenti'
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Chart + Narrative */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Revenue chart — 2/3 */}
        <div className="lg:col-span-2 rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          <PanelHeader label="Revenue mensile">
            <div className="flex items-center gap-4 text-[10px] font-medium text-[--km-text-muted]">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-5 rounded-sm bg-indigo-500 opacity-80" />
                Anno corrente
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-px w-5 border-t border-dashed border-purple-500" />
                Anno prec.
              </span>
            </div>
          </PanelHeader>
          <div className="h-60">
            <RevenueChart data={chartData} />
          </div>
        </div>

        {/* AI Narrative — 1/3 */}
        <div className="lg:col-span-1 min-h-[280px]">
          <NarrativeCard />
        </div>
      </div>

      {/* Breakdown ordinato per cliente e service line */}
      <OrdersBreakdownCharts
        byCustomer={kpis?.orders_by_customer ?? []}
        byServiceLine={kpis?.orders_by_service_line ?? []}
      />

      {/* Breakdown per Business Unit — visibile solo se BU configurate */}
      {selectedBuId === null && (
        <div className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-6 shadow-[0_4px_24px_rgba(0,0,0,0.4)]">
          <PanelHeader label="Revenue per Business Unit" />
          <BUBreakdownChart metric="revenue" period="ytd" year={typeof selectedYear === 'number' ? selectedYear : undefined} />
        </div>
      )}

      {kpis && kpis.coverage_score_pct < 80 && (
        <AlertBanner
          severity="warning"
          message={`Copertura dati: ${kpis.coverage_score_pct.toFixed(0)}% dei KPI calcolabili. Caricare più dati per ottenere un quadro completo.`}
        />
      )}
    </section>
  )
}
